<template>
  <FlashNotifications ref="flash" />

  <!-- ═══════════════ MOBILE ═══════════════ -->
  <div class="sm:hidden bg-gray-50 dark:bg-[#0d1117] min-h-app-screen">
    <div class="sticky top-0 z-10 bg-white dark:bg-[#1C2128] border-b border-gray-100 dark:border-gray-700">
      <div class="flex items-center px-4 h-14">
        <h1 class="text-[22px] font-bold text-gray-900 dark:text-white tracking-tight leading-none">
          {{ I18N.website_content.account_settings.dev_console_tab.title }}
        </h1>
      </div>
    </div>

    <div class="pb-20">
      <template v-if="!useSettingsStoreObj().init_data_loaded">
        <div class="animate-pulse px-4 pt-6 space-y-3">
          <div class="h-2.5 w-16 bg-gray-200 dark:bg-gray-700 rounded mx-0 mb-2"></div>
          <div class="bg-white dark:bg-[#1C2128] rounded-2xl overflow-hidden p-4 space-y-3">
            <div class="h-2.5 w-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div class="h-11 w-full bg-gray-100 dark:bg-gray-800 rounded-lg"></div>
          </div>
          <div class="h-2.5 w-24 bg-gray-200 dark:bg-gray-700 rounded mx-0 mb-2"></div>
          <div class="bg-white dark:bg-[#1C2128] rounded-2xl overflow-hidden px-4 py-3 flex items-center gap-3">
            <div class="w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-800 flex-shrink-0"></div>
            <div class="space-y-1.5">
              <div class="h-2.5 w-28 bg-gray-200 dark:bg-gray-700 rounded"></div>
              <div class="h-2 w-44 bg-gray-100 dark:bg-gray-800 rounded"></div>
            </div>
          </div>
          <div class="bg-white dark:bg-[#1C2128] rounded-2xl overflow-hidden divide-y divide-gray-100 dark:divide-gray-700">
            <div class="px-4 py-3 flex items-center justify-between">
              <div class="space-y-1.5">
                <div class="h-2.5 w-28 bg-gray-200 dark:bg-gray-700 rounded"></div>
                <div class="h-2 w-40 bg-gray-100 dark:bg-gray-800 rounded"></div>
              </div>
              <div class="h-8 w-20 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
            </div>
          </div>
        </div>
      </template>
      <template v-else>
        <p class="px-4 pt-6 pb-2 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">API Key</p>
        <div class="bg-white dark:bg-[#1C2128] rounded-2xl mx-4 overflow-hidden">
          <div class="px-4 py-4">
            <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">Your API Key</label>
            <div class="relative">
              <input :type="showApiKey ? 'text' : 'password'"
                     :value="apiKey"
                     readonly
                     class="w-full px-3.5 py-2.5 pr-20 text-sm font-mono border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-[#161b22] text-gray-900 dark:text-gray-100 focus:outline-none" />
              <div class="absolute inset-y-0 right-0 flex items-center pr-2 gap-0.5">
                <button type="button" @click="copyApiKey"
                        class="p-1.5 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M15.666 3.888A2.25 2.25 0 0 0 13.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 0 1-.75.75H9a.75.75 0 0 1-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 0 1-2.25 2.25H6.75A2.25 2.25 0 0 1 4.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 0 1 1.927-.184"/></svg>
                </button>
                <button type="button" @click="showApiKey = !showApiKey"
                        class="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
                  <svg v-if="showApiKey" class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88"/></svg>
                  <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"/><path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"/></svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        <p class="px-4 pt-5 pb-2 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Documentation</p>
        <div class="bg-white dark:bg-[#1C2128] rounded-2xl mx-4 overflow-hidden">
          <a href="/api/docs/" target="_blank" rel="noopener" data-turbo="false"
             class="px-4 py-3 flex items-center justify-between gap-3 active:bg-gray-50 dark:active:bg-gray-800">
            <div class="flex items-center gap-3 min-w-0">
              <div class="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center flex-shrink-0">
                <svg class="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25"/></svg>
              </div>
              <div class="min-w-0">
                <p class="text-sm font-medium text-gray-900 dark:text-white">API documentation</p>
                <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Authentication, code examples and errors</p>
              </div>
            </div>
            <svg class="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"/></svg>
          </a>
        </div>

        <p class="px-4 pt-5 pb-2 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Danger</p>
        <div class="bg-white dark:bg-[#1C2128] rounded-2xl mx-4 overflow-hidden divide-y divide-gray-100 dark:divide-gray-700">
          <div class="px-4 py-3 flex items-center justify-between gap-3">
            <div>
              <p class="text-sm font-medium text-gray-900 dark:text-white">Regenerate key</p>
              <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Invalidates your current key immediately</p>
            </div>
            <button @click="regenerateApiKey" :disabled="regenerating"
                    class="flex-shrink-0 px-3 py-1.5 text-xs font-semibold text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors disabled:opacity-50">
              {{ regenerating ? 'Working...' : 'Regenerate' }}
            </button>
          </div>
        </div>
      </template>
    </div>
  </div>

  <!-- ═══════════════ DESKTOP ═══════════════ -->
  <div class="hidden sm:block min-h-app-screen bg-gray-50 dark:bg-[#0d1117]">

    <!-- Desktop skeleton -->
    <template v-if="!useSettingsStoreObj().init_data_loaded">
      <div class="animate-pulse">
        <div class="bg-white dark:bg-[#1C2128] border-b border-gray-200 dark:border-gray-700 sticky top-14 z-10">
          <div class="max-w-5xl mx-auto px-6 flex items-center h-14">
            <div class="flex items-center gap-2.5">
              <div class="w-7 h-7 rounded-lg bg-gray-100 dark:bg-gray-800 flex-shrink-0"></div>
              <div class="space-y-1.5">
                <div class="h-3 w-16 bg-gray-200 dark:bg-gray-700 rounded"></div>
                <div class="h-2.5 w-28 bg-gray-100 dark:bg-gray-800 rounded"></div>
              </div>
            </div>
          </div>
        </div>
        <div class="max-w-5xl mx-auto px-6 py-6">
          <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
            <div class="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-3">
              <div class="w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-800 flex-shrink-0"></div>
              <div class="space-y-1.5">
                <div class="h-3 w-24 bg-gray-200 dark:bg-gray-700 rounded"></div>
                <div class="h-2.5 w-36 bg-gray-100 dark:bg-gray-800 rounded"></div>
              </div>
            </div>
            <div class="flex divide-x divide-gray-100 dark:divide-gray-700">
              <div class="flex-1 px-6 py-5 space-y-4">
                <div class="h-2.5 w-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
                <div class="h-11 w-full bg-gray-100 dark:bg-gray-800 rounded-lg"></div>
                <div class="pt-4 border-t border-gray-100 dark:border-gray-800 space-y-2">
                  <div class="h-2.5 w-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
                  <div class="h-2 w-2/3 bg-gray-100 dark:bg-gray-800 rounded"></div>
                  <div class="h-16 w-full bg-gray-100 dark:bg-gray-800 rounded-lg"></div>
                  <div class="h-2 w-1/2 bg-gray-100 dark:bg-gray-800 rounded"></div>
                  <div class="h-2 w-1/3 bg-gray-100 dark:bg-gray-800 rounded"></div>
                </div>
                <div class="pt-2 border-t border-gray-100 dark:border-gray-800 space-y-2">
                  <div class="h-2.5 w-28 bg-gray-200 dark:bg-gray-700 rounded"></div>
                  <div class="h-2 w-full bg-gray-100 dark:bg-gray-800 rounded"></div>
                  <div class="h-2 w-3/4 bg-gray-100 dark:bg-gray-800 rounded"></div>
                  <div class="pt-1">
                    <div class="h-9 w-36 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
                  </div>
                </div>
              </div>
              <div class="w-72 flex-shrink-0 px-6 py-5 space-y-4">
                <div class="flex items-center gap-2.5">
                  <div class="w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-800 flex-shrink-0"></div>
                  <div class="space-y-1.5">
                    <div class="h-3 w-24 bg-gray-200 dark:bg-gray-700 rounded"></div>
                    <div class="h-2 w-16 bg-gray-100 dark:bg-gray-800 rounded"></div>
                  </div>
                </div>
                <div class="h-2.5 w-full bg-gray-100 dark:bg-gray-800 rounded"></div>
                <div class="h-2.5 w-3/4 bg-gray-100 dark:bg-gray-800 rounded"></div>
                <div class="space-y-3 pt-1">
                  <div v-for="j in 3" :key="j" class="flex items-center gap-2.5">
                    <div class="w-5 h-5 rounded-full bg-gray-100 dark:bg-gray-800 flex-shrink-0"></div>
                    <div class="h-2.5 w-40 bg-gray-100 dark:bg-gray-800 rounded"></div>
                  </div>
                </div>
                <div class="mt-5 h-14 w-full rounded-lg border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Desktop loaded -->
    <template v-else>
      <!-- Sub-page header -->
      <div class="bg-white dark:bg-[#1C2128] border-b border-gray-200 dark:border-gray-700 sticky top-14 z-10">
        <div class="max-w-5xl mx-auto px-6 flex items-center h-14">
          <div class="flex items-center gap-2.5">
            <div class="w-7 h-7 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center flex-shrink-0">
              <svg class="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 5.25a3 3 0 0 1 3 3m3 0a6 6 0 0 1-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 0 1 21.75 8.25Z"/>
              </svg>
            </div>
            <div>
              <h1 class="text-sm font-semibold text-gray-900 dark:text-white leading-none">{{ I18N.website_content.account_settings.dev_console_tab.title }}</h1>
              <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 leading-none">Manage your API credentials</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Content -->
      <div class="max-w-5xl mx-auto px-6 py-6">
        <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">

          <!-- Card header -->
          <div class="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-3">
            <div class="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center flex-shrink-0">
              <svg class="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 5.25a3 3 0 0 1 3 3m3 0a6 6 0 0 1-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 0 1 21.75 8.25Z"/>
              </svg>
            </div>
            <div>
              <h2 class="text-sm font-semibold text-gray-900 dark:text-white leading-none">{{ I18N.website_content.account_settings.dev_console_tab.title }}</h2>
              <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 leading-none">Use this key to authenticate API requests</p>
            </div>
          </div>

          <!-- Two-panel body -->
          <div class="flex divide-x divide-gray-100 dark:divide-gray-700">

            <!-- Left: API key + regenerate -->
            <div class="flex-1 px-6 py-5 space-y-5">
              <div>
                <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">Your API Key</label>
                <div class="relative">
                  <input :type="showApiKey ? 'text' : 'password'"
                         :value="apiKey"
                         readonly
                         class="w-full px-3.5 py-2.5 pr-20 text-sm font-mono border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-[#161b22] text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all" />
                  <div class="absolute inset-y-0 right-0 flex items-center pr-2 gap-0.5">
                    <button type="button" @click="copyApiKey"
                            class="p-1.5 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors" title="Copy to clipboard">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M15.666 3.888A2.25 2.25 0 0 0 13.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 0 1-.75.75H9a.75.75 0 0 1-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 0 1-2.25 2.25H6.75A2.25 2.25 0 0 1 4.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 0 1 1.927-.184"/></svg>
                    </button>
                    <button type="button" @click="showApiKey = !showApiKey"
                            class="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
                      <svg v-if="showApiKey" class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88"/></svg>
                      <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"/><path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"/></svg>
                    </button>
                  </div>
                </div>
              </div>

              <!-- Quick start: a working request with this account's key -->
              <div class="pt-4 border-t border-gray-100 dark:border-gray-700">
                <div class="flex items-center justify-between mb-1">
                  <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Quick start</p>
                  <a href="/api/docs/#authenticate-your-requests" target="_blank" rel="noopener" data-turbo="false"
                     class="text-xs font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 transition-colors">More examples</a>
                </div>
                <p class="text-xs text-gray-400 dark:text-gray-500 mb-3 leading-relaxed">Test your key with this request. It returns the account the key belongs to.</p>
                <div class="relative rounded-lg bg-[#0f2341] border border-[#1b3561]">
                  <pre class="px-4 py-3 pr-12 text-[12px] leading-relaxed font-mono text-blue-100 overflow-x-auto whitespace-pre"><code>{{ quickStartDisplay }}</code></pre>
                  <button type="button" @click="copyQuickStart" title="Copy command"
                          class="absolute top-2 right-2 p-1.5 rounded-md text-blue-200/70 hover:text-white hover:bg-white/10 transition-colors">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M15.666 3.888A2.25 2.25 0 0 0 13.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 0 1-.75.75H9a.75.75 0 0 1-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 0 1-2.25 2.25H6.75A2.25 2.25 0 0 1 4.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 0 1 1.927-.184"/></svg>
                  </button>
                </div>
                <dl class="mt-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-xs">
                  <dt class="text-gray-400 dark:text-gray-500">Base URL</dt>
                  <dd class="font-mono text-gray-700 dark:text-gray-200 truncate">{{ apiBaseUrl }}</dd>
                  <dt class="text-gray-400 dark:text-gray-500">Header</dt>
                  <dd class="font-mono text-gray-700 dark:text-gray-200">X-API-KEY</dd>
                </dl>
              </div>

              <div class="pt-4 border-t border-gray-100 dark:border-gray-700">
                <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Regenerate Key</p>
                <p class="text-xs text-gray-400 dark:text-gray-500 mb-3 leading-relaxed">Generating a new key will immediately invalidate your current one. Any integrations using the old key will stop working.</p>
                <button @click="regenerateApiKey" :disabled="regenerating"
                        class="inline-flex items-center gap-2 px-4 py-2 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400 text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                  <svg v-if="regenerating" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                  <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99"/></svg>
                  {{ regenerating ? 'Regenerating...' : 'Regenerate Key' }}
                </button>
              </div>
            </div>

            <!-- Right: info panel -->
            <div class="w-72 flex-shrink-0 px-6 py-5 bg-white dark:bg-[#1C2128]">
              <div class="flex items-center gap-2.5 mb-3">
                <div class="w-8 h-8 rounded-lg bg-purple-50 dark:bg-purple-900/20 flex items-center justify-center flex-shrink-0">
                  <svg class="w-4 h-4 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="m6.75 7.5 3 2.25-3 2.25m4.5 0h3m-9 8.25h13.5A2.25 2.25 0 0 0 21 18V6a2.25 2.25 0 0 0-2.25-2.25H5.25A2.25 2.25 0 0 0 3 6v12a2.25 2.25 0 0 0 2.25 2.25Z"/>
                  </svg>
                </div>
                <div>
                  <p class="text-sm font-semibold text-gray-900 dark:text-white leading-none">About API keys</p>
                  <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 leading-none">How to use your key</p>
                </div>
              </div>
              <p class="text-xs text-gray-500 dark:text-gray-400 mb-4 leading-relaxed">Use your API key to authenticate requests to the API from your own applications or integrations.</p>
              <ul class="space-y-3">
                <li class="flex items-start gap-2.5">
                  <div class="w-5 h-5 rounded-full bg-purple-50 dark:bg-purple-900/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg class="w-3 h-3 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg>
                  </div>
                  <p class="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">Pass the key in the <span class="font-mono bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded text-[11px]">X-API-KEY</span> header</p>
                </li>
                <li class="flex items-start gap-2.5">
                  <div class="w-5 h-5 rounded-full bg-purple-50 dark:bg-purple-900/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg class="w-3 h-3 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg>
                  </div>
                  <p class="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">Never expose your key in client-side code</p>
                </li>
                <li class="flex items-start gap-2.5">
                  <div class="w-5 h-5 rounded-full bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg class="w-3 h-3 text-amber-500 dark:text-amber-400" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"/></svg>
                  </div>
                  <p class="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">Regenerating invalidates all existing integrations immediately</p>
                </li>
              </ul>

              <a href="/api/docs/" target="_blank" rel="noopener" data-turbo="false"
                 class="group mt-5 flex items-center gap-3 px-3.5 py-3 rounded-lg border border-gray-100 dark:border-gray-700 hover:border-blue-200 dark:hover:border-blue-800 hover:bg-blue-50/50 dark:hover:bg-blue-900/10 transition-colors">
                <div class="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center flex-shrink-0">
                  <svg class="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25"/></svg>
                </div>
                <div class="min-w-0 flex-1">
                  <p class="text-sm font-semibold text-gray-900 dark:text-white leading-none group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">API documentation</p>
                  <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-1 leading-snug">Authentication, code examples and errors</p>
                </div>
                <svg class="w-4 h-4 text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 flex-shrink-0 transition-colors" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"/></svg>
              </a>
            </div>

          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { useSettingsStore } from 'controllers/vue_components/settings/v1/store'
import RequestMixin from 'controllers/vue_components/mixins/request'
import Constants from 'controllers/vue_components/mixins/constants'
import FlashNotifications from 'controllers/vue_components/flash_toast'
import Formatting from 'controllers/vue_components/mixins/formatting'

export default {
    name: 'DevelopersApiKey',
    props: [],
    mixins: [RequestMixin, Constants, Formatting],
    components: { FlashNotifications },

    data() {
        return {
            showApiKey: false,
            regenerating: false,
            apiKey: this.useSettingsStoreObj().current_user.api_key || '',
        }
    },

    computed: {
        // APPLICATION_URL comes from the Constants mixin, so dev shows localhost and production timelydo.com.
        apiBaseUrl() {
            return `${(this.APPLICATION_URL || window.location.origin).replace(/\/$/, '')}/api/v1`
        },

        quickStartDisplay() {
            return this.quickStartCommand(this.showApiKey ? this.apiKey : '•'.repeat(16))
        },
    },

    methods: {
        useSettingsStoreObj() {
            return useSettingsStore()
        },

        quickStartCommand(key) {
            return `curl ${this.apiBaseUrl}/user \\\n  -H "X-API-KEY: ${key}"`
        },

        // Copies the command with the real key, even while the key is hidden on screen.
        copyQuickStart() {
            this.copyTextToClipboard(this.quickStartCommand(this.apiKey))
            this.$refs.flash.show('Command copied', 'notice')
        },

        async regenerateApiKey() {
            this.regenerating = true
            try {
                const res = await this.axiosRequest('PUT', '/api/v1/users/regenerate_api_key', {}, true)
                if (res.js_error === false && res.response.success) {
                    this.apiKey = res.response.data.api_key
                }
            } catch (error) {
                console.error(this.I18N.website_content.account_settings.developers_console.error_occured, error)
            } finally {
                this.regenerating = false
            }
        },

        copyApiKey() {
            this.copyTextToClipboard(this.apiKey)
            this.$refs.flash.show(this.I18N.website_content.account_settings.developers_console.api_key_copied, 'notice')
        },
    },

    created() {
        this.$watch(
            () => this.useSettingsStoreObj().init_data_loaded,
            (loaded) => {
                if (loaded) this.apiKey = this.useSettingsStoreObj().current_user.api_key || ''
            }
        )
    },
}
</script>
